import { useState, useEffect } from "react";
import type { Activity, ActivitySummary } from "../utils/dataStore";

interface Props {
  activity: Activity;
  onSave: (summary: ActivitySummary) => void;
  onClose: () => void;
}

const STAR_LABELS: Record<number, { label: string; color: string }> = {
  1: { label: "很差",   color: "text-red-500" },
  2: { label: "较差",   color: "text-orange-500" },
  3: { label: "一般",   color: "text-amber-500" },
  4: { label: "较好",   color: "text-lime-600" },
  5: { label: "非常棒", color: "text-green-600" },
};

const HIGHLIGHT_PRESETS = ["组织流程顺畅", "会员参与度高", "现场氛围热烈", "签到效率高", "宣传到位", "志愿者表现出色"];
const IMPROVE_PRESETS  = ["报名流程繁琐", "场地容量不足", "通知时间太短", "到场率偏低", "经费超出预算", "后勤支持不足"];

export default function ActivitySummaryModal({ activity, onSave, onClose }: Props) {
  const existing = activity.activitySummary;
  const [rating, setRating]             = useState(existing?.rating ?? 4);
  const [hoverRating, setHoverRating]   = useState(0);
  const [summary, setSummary]           = useState(existing?.summary ?? "");
  const [highlights, setHighlights]     = useState(existing?.highlights ?? "");
  const [improvements, setImprovements] = useState(existing?.improvements ?? "");
  const [nextSuggestion, setNextSugg]   = useState(existing?.nextSuggestion ?? "");
  const [saved, setSaved]               = useState(false);

  useEffect(() => {
    if (existing) {
      setRating(existing.rating);
      setSummary(existing.summary);
      setHighlights(existing.highlights);
      setImprovements(existing.improvements);
      setNextSugg(existing.nextSuggestion);
    }
  }, [existing]);

  const toggleChip = (
    value: string,
    current: string,
    setter: (v: string) => void,
  ) => {
    const parts = current.split("；").map((s) => s.trim()).filter(Boolean);
    const idx = parts.indexOf(value);
    const next = idx >= 0 ? parts.filter((_, i) => i !== idx) : [...parts, value];
    setter(next.join("；"));
  };

  const isChipActive = (value: string, current: string) =>
    current.split("；").map((s) => s.trim()).includes(value);

  const handleSave = () => {
    const data: ActivitySummary = {
      rating,
      summary: summary.trim(),
      highlights: highlights.trim(),
      improvements: improvements.trim(),
      nextSuggestion: nextSuggestion.trim(),
      submittedAt: new Date().toISOString().slice(0, 10),
    };
    onSave(data);
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      onClose();
    }, 900);
  };

  const displayRating = hoverRating || rating;
  const starInfo = STAR_LABELS[displayRating] ?? STAR_LABELS[3];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-[640px] max-h-[92vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-file-list-3-line text-teal-500 text-lg"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-800">活动总结</h3>
              <p className="text-xs text-gray-400 mt-0.5 max-w-[360px] truncate">
                {activity.title} · {activity.date}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100 flex-shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ── Star Rating ── */}
          <div className="bg-gray-50 rounded-2xl p-5">
            <p className="text-sm font-semibold text-gray-700 mb-3">活动整体评价</p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onMouseEnter={() => setHoverRating(s)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(s)}
                    className="cursor-pointer transition-transform hover:scale-110"
                  >
                    <i
                      className={`ri-star-${s <= displayRating ? "fill" : "line"} text-2xl transition-colors ${
                        s <= displayRating ? "text-amber-400" : "text-gray-200"
                      }`}
                    ></i>
                  </button>
                ))}
              </div>
              <span className={`text-sm font-semibold ${starInfo.color}`}>
                {starInfo.label}
              </span>
              <span className="text-xs text-gray-400">（{displayRating} 分）</span>
            </div>
          </div>

          {/* ── Summary text ── */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              总体总结
              <span className="ml-1 text-gray-400 font-normal">（活动概况与整体回顾）</span>
            </label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="简要描述本次活动的整体情况，包括活动规模、氛围、会员反应等…"
              rows={3}
              maxLength={500}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 bg-white resize-none"
            />
            <p className="text-xs text-gray-400 text-right mt-1">{summary.length}/500</p>
          </div>

          {/* ── Highlights ── */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              活动亮点
              <span className="ml-1 text-gray-400 font-normal">（快速选择或自行填写）</span>
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {HIGHLIGHT_PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => toggleChip(p, highlights, setHighlights)}
                  className={`px-3 py-1.5 rounded-xl text-xs cursor-pointer transition-all whitespace-nowrap border ${
                    isChipActive(p, highlights)
                      ? "bg-teal-600 text-white border-teal-600"
                      : "bg-white text-gray-600 border-gray-200 hover:border-teal-300"
                  }`}
                >
                  {isChipActive(p, highlights) && <i className="ri-check-line mr-1 text-xs"></i>}
                  {p}
                </button>
              ))}
            </div>
            <input
              value={highlights}
              onChange={(e) => setHighlights(e.target.value)}
              placeholder="如：组织流程顺畅；会员参与度高（用分号分隔多条）"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 bg-white"
            />
          </div>

          {/* ── Improvements ── */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              待改进事项
              <span className="ml-1 text-gray-400 font-normal">（找到不足，下次做得更好）</span>
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {IMPROVE_PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => toggleChip(p, improvements, setImprovements)}
                  className={`px-3 py-1.5 rounded-xl text-xs cursor-pointer transition-all whitespace-nowrap border ${
                    isChipActive(p, improvements)
                      ? "bg-orange-500 text-white border-orange-500"
                      : "bg-white text-gray-600 border-gray-200 hover:border-orange-300"
                  }`}
                >
                  {isChipActive(p, improvements) && <i className="ri-check-line mr-1 text-xs"></i>}
                  {p}
                </button>
              ))}
            </div>
            <input
              value={improvements}
              onChange={(e) => setImprovements(e.target.value)}
              placeholder="如：到场率偏低；通知时间太短（用分号分隔多条）"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400 bg-white"
            />
          </div>

          {/* ── Next Suggestion ── */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              下次活动建议
              <span className="ml-1 text-gray-400 font-normal">（留给下一次活动负责人的备注）</span>
            </label>
            <textarea
              value={nextSuggestion}
              onChange={(e) => setNextSugg(e.target.value)}
              placeholder="对下次举办此类活动有什么建议？如：提前2周发布报名；预留更多座位区等…"
              rows={2}
              maxLength={300}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 bg-white resize-none"
            />
          </div>

          {/* ── Summary preview if existing ── */}
          {existing && (
            <div className="bg-teal-50 rounded-xl px-4 py-3 flex items-center gap-2">
              <i className="ri-check-double-line text-teal-600 text-sm"></i>
              <p className="text-xs text-teal-700">
                已有总结记录（{existing.submittedAt}），保存后将覆盖原有内容。
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-white">
          <p className="text-xs text-gray-400">总结保存后可随时再次编辑</p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap"
            >
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={saved}
              className={`flex items-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-xl cursor-pointer transition-all whitespace-nowrap ${
                saved
                  ? "bg-green-500 text-white"
                  : "bg-teal-600 text-white hover:bg-teal-700"
              }`}
            >
              {saved ? (
                <><i className="ri-check-line"></i> 已保存</>
              ) : (
                <><i className="ri-save-line"></i> 保存总结</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
